import {
  PROPOSALS_WARM_STALE_TIME_MS,
  useProposals,
  usePrefetchProposals,
  type Proposal,
} from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildHistoryCarrierOptions,
  filterProposalsForHistoryView,
} from "../utils/quoteHistoryCarriers";

import { useProposalsWithCarrierNames } from "./useProposalsWithCarrierNames";

// ---------------------------------------------------------------------------
// Quote History (ADR-031) — owns the carrier-selection dialog and the applied
// carrier filter. Whether the view is on lives in useQuoteHistoryView, above the
// proposal fetch that this hook's inputs come out of. Extracted to keep the
// Quotes component below the complexity limit.
// ---------------------------------------------------------------------------

export const useQuoteHistory = ({
  rfpId,
  ecoRfpId,
  selectedCoverageType,
  selectedBpgId,
  proposals,
  quoteHistoryEnabled,
  effectiveShowHistory,
  selectedHistoryCarrierIds,
  enterHistory,
  exitHistory,
}: {
  rfpId: string | undefined;
  ecoRfpId: string;
  selectedCoverageType: CoverageTypeEnum | undefined;
  /** The SBS page on screen: its RFP-requested plan group id (CD-1793 scoping). */
  selectedBpgId: string | undefined;
  proposals: Proposal[];
  /** ppx-quote-history: gates the prefetch, so a disabled feature costs no request. */
  quoteHistoryEnabled: boolean;
  effectiveShowHistory: boolean;
  selectedHistoryCarrierIds: string[];
  enterHistory: (carrierIds: string[]) => void;
  exitHistory: () => void;
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- inferred; extracted interface would be large
}) => {
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // All-version proposals powering the carrier picker, fetched only while the
  // dialog is open. Names are resolved so the picker shows real carrier names,
  // and only carriers with a requote are listed.
  const { data: historyProposalsRaw = [], isLoading: isLoadingCarriers } =
    useProposals(
      ecoRfpId,
      {
        enabled: historyDialogOpen && !!ecoRfpId,
        // Matches the prefetch, so reopening the dialog reads the warmed entry instead
        // of background-refetching a list that has not changed.
        staleTime: PROPOSALS_WARM_STALE_TIME_MS,
      },
      "all",
    );
  // Warmed ahead of the dialog, so the picker has its carriers on first paint. The
  // dialog's query above reads the same cache entry.
  const prefetchHistoryProposals = usePrefetchProposals(ecoRfpId, "all");

  // Warm on mount rather than waiting for intent. This scope is a separate request from
  // the board's, so a trigger closer to the click — hovering the button — still leaves it
  // in flight when the dialog opens. Starting here runs it alongside the board fetch the
  // analyst is already waiting on, and they read the board for seconds before reaching for
  // history. The toolbar's hover handler stays as a second chance for when the entry has
  // been evicted or invalidated by a requote action.
  useEffect(() => {
    if (!quoteHistoryEnabled) return;
    prefetchHistoryProposals();
  }, [quoteHistoryEnabled, prefetchHistoryProposals]);

  const historyProposals = useProposalsWithCarrierNames(
    rfpId,
    historyProposalsRaw,
  );
  const historyCarrierOptions = useMemo(
    () =>
      buildHistoryCarrierOptions(
        historyProposals,
        selectedCoverageType,
        selectedBpgId,
      ),
    [historyProposals, selectedCoverageType, selectedBpgId],
  );

  // In the history view, reveal the picked carriers' past quotes without taking the rest of
  // the board away: every carrier keeps its active quote (CD-1789). Off the history view the
  // board is unfiltered.
  const gridProposals = useMemo(
    () =>
      effectiveShowHistory
        ? filterProposalsForHistoryView(
            proposals,
            selectedHistoryCarrierIds,
            selectedCoverageType,
            selectedBpgId,
          )
        : proposals,
    [
      proposals,
      effectiveShowHistory,
      selectedHistoryCarrierIds,
      selectedCoverageType,
      selectedBpgId,
    ],
  );

  const handleHistoryButtonClick = useCallback(() => {
    if (effectiveShowHistory) {
      exitHistory();
    } else {
      setHistoryDialogOpen(true);
    }
  }, [effectiveShowHistory, exitHistory]);

  const handleHistoryView = useCallback(
    (carrierIds: string[]) => {
      enterHistory(carrierIds);
      setHistoryDialogOpen(false);
    },
    [enterHistory],
  );

  return {
    historyDialogOpen,
    isLoadingCarriers,
    prefetchHistoryProposals,
    setHistoryDialogOpen,
    historyCarrierOptions,
    gridProposals,
    handleHistoryButtonClick,
    handleHistoryView,
  };
};
